import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/components/views/published/usePublishedSnapshotQuery';
import type { ItemType } from '@/lib/api/types/itemType';
import { itemTypesQueryOptions } from '@/lib/queries/common/useItemTypesQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { associateById } from '@/lib/utils/common/associateById';

export const useItemTypesQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.itemTypes.all,
    getSnapshot: () => publishedContext?.itemTypes,
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};

export const useItemTypesByIdQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    select: associateById,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery<ItemType[], Record<number, ItemType>>({
    baseKey: commonQueryKeys.itemTypes.all,
    getSnapshot: () => publishedContext?.itemTypes,
    select: (data) => (data ? associateById(data) : {}),
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};
